import React, { useState, useEffect } from 'react';
import groupService from '../../api/groupService';

const PostTagModal = ({ isOpen, onClose, groupId, editingTag, onTagSaved }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    name: ''
  });

  const [errors, setErrors] = useState({});

  // Reset form when modal opens or editing tag changes
  useEffect(() => {
    if (isOpen) {
      if (editingTag) {
        setFormData({
          name: editingTag.name || ''
        });
      } else {
        setFormData({
          name: ''
        });
      }
      setErrors({});
      setError('');
    }
  }, [isOpen, editingTag]);

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Tag name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Tag name must be at least 2 characters';
    } else if (formData.name.trim().length > 30) {
      newErrors.name = 'Tag name must be 30 characters or less';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const tagData = {
        name: formData.name.trim(),
        icon: '📝', // Default icon
        color: '#5669FF' // Default color
      };

      let response;
      if (editingTag) {
        response = await groupService.updatePostTag(groupId, editingTag.id, tagData);
      } else {
        response = await groupService.createPostTag(groupId, tagData);
      }
      
      if (response.success) {
        const savedTag = response.data;
        if (onTagSaved) {
          onTagSaved(savedTag);
        }
        onClose();
      } else {
        setError(response.message || `Failed to ${editingTag ? 'update' : 'create'} tag`);
      }
    } catch (err) {
      console.error('Error saving tag:', err);
      setError(err.message || `Failed to ${editingTag ? 'update' : 'create'} tag`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-ph-capture-attribute-disabled="true">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {editingTag ? 'Edit Tag' : 'Create New Tag'}
          </h2>
          <button 
            className="text-gray-400 hover:text-gray-600 text-3xl font-light leading-none transition-colors"
            onClick={handleClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-800 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6">
            <label htmlFor="tagName" className="block mb-2 font-medium text-gray-700">
              Tag Name *
            </label>
            <input
              type="text"
              id="tagName"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`w-full px-3 py-2.5 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
              placeholder="e.g., Newsletter, Torah, Chesed"
              disabled={loading}
              maxLength={30}
              autoFocus
            />
            {errors.name && <span className="text-red-600 text-xs mt-1 block">{errors.name}</span>}
          </div>

          <div className="flex items-center justify-between gap-3 pt-4 border-t border-gray-200">
            <button 
              type="button"
              className="px-6 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 hover:border-gray-400 transition-all hover:-translate-y-px disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium shadow-md hover:from-indigo-700 hover:to-purple-700 transition-all hover:-translate-y-px hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              disabled={loading}
            >
              {loading ? (editingTag ? 'Updating...' : 'Creating...') : (editingTag ? 'Update Tag' : 'Create Tag')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostTagModal;
